import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { flip, offset, useFloating } from '@floating-ui/react-dom-interactions';
import { useCallback, useState } from 'react';

export type AddToListMenuReturn = {
  readonly floatingProps: ReturnType<typeof useFloating>;
  readonly handleCloseListMenu: () => void;
  readonly handleOpenListMenu: () => void;
  readonly isActionsMenuOpen: boolean;
  readonly isListMenuOpen: boolean;
  readonly setIsActionsMenuOpen: (value: boolean) => void;
  readonly toggleActionsMenuOpen: () => void;
};

type AddToListMenuProps = {
  readonly analyticsSection: string;
  readonly isSaved: boolean;
};

export const useAddToListMenu = ({
  analyticsSection,
  isSaved,
}: AddToListMenuProps): AddToListMenuReturn => {
  const { track } = useAnalytics();

  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState<boolean>(false);
  const toggleActionsMenuOpen = useCallback(() => {
    setIsActionsMenuOpen((previous) => !previous);
  }, []);

  const [isListMenuOpen, setIsListMenuOpen] = useState(false);

  const floatingProps = useFloating({
    middleware: [offset(4), flip()],
    open: isListMenuOpen,
    placement: isSaved && isActionsMenuOpen ? 'right' : 'bottom',
    strategy: 'fixed',
  });

  const handleCloseListMenu = useCallback(() => {
    if (isListMenuOpen) {
      track('menu', {
        action: 'tapped',
        menu_name: 'save_user_menu',
        section: analyticsSection,
      });

      setIsListMenuOpen(false);
    }
  }, [analyticsSection, isListMenuOpen, track]);

  const handleOpenListMenu = useCallback(() => {
    if (!isListMenuOpen) {
      track('menu', {
        action: 'tapped',
        menu_name: 'save_user_menu',
        section: analyticsSection,
      });
      setIsListMenuOpen(true);
    }
  }, [analyticsSection, isListMenuOpen, track]);

  return {
    floatingProps,
    handleCloseListMenu,
    handleOpenListMenu,
    isActionsMenuOpen,
    isListMenuOpen,
    setIsActionsMenuOpen,
    toggleActionsMenuOpen,
  };
};
